<template>
  <c-box v-chakra="{ '.stale': { opacity: '0.2' } }" :pt="{ base: '42px', md: '92px' }">
    <c-flex wrap="wrap">
      <c-box
        :flex="{ base: '0 0 100%', md: '0 0 210px' }"
        :w="{ base: '100%', md: '210px' }"
        pr="sm"
      >
        <CategoryFilter></CategoryFilter>
      </c-box>
      <c-box :flex="{ base: '0 0 100%', md: '1' }">
        <c-box class="row d-none d-md-flex" mb="48px">
          <c-box class="col-md-7">
            <c-box
              h="203px"
              bgImage="url('/img/products/search_header.jpg')"
              bgPos="top center"
              bgSize="cover"
              rounded="base"
              position="relative"
            >
              <c-box position="absolute" left="61px" bottom="36px">
                <c-heading
                  as="h1"
                  color="white"
                  fontSize="45px"
                  lineHeight="1.1"
                  fontWeight="400"
                  data-cy="products-title"
                >
                  {{ $t("productSearch.bannerHeader") }}
                </c-heading>
              </c-box>
            </c-box>
          </c-box>
          <c-box class="col-md-5 d-flex align-items-center">
            <c-box
              :pl="{ base: '0', lg: '24px', xl: '48px' }"
              maxWidth="410px"
              v-chakra="{ q: { fontStyle: 'italic' } }"
            >
              <span v-html="$t('productSearch.bannerTextHtml')"></span>
            </c-box>
          </c-box>
        </c-box>
        <c-box class="row d-flex d-md-none" mt="lg" mb="xl">
          <c-box class="col">
            <c-heading
              as="h1"
              color="brand.purpleDark"
              fontSize="36px"
              lineHeight="1.1"
              fontWeight="400"
            >
              {{ $t("productSearch.bannerHeader") }}
            </c-heading>
          </c-box>
        </c-box>
        <c-box class="row">
          <c-box class="col-md-12" :mb="{ base: '2xl', md: '4xl' }">
            <c-flex align="center" flex="1">
              <c-box :d="{ base: 'none', md: 'block' }">
                <c-icon name="search" size="3rem" mr="md" color="brand.purple" />
              </c-box>
              <c-box width="100%" max-width="34rem">
                <c-input
                  id="searchTerm"
                  position="relative"
                  px="0"
                  pb="15px"
                  :is-full-width="true"
                  bg="transparent"
                  color="brand.purple"
                  :_placeholder="{ color: 'brand.purple' }"
                  border-radius="0"
                  border-width="0px"
                  border-bottom-width="1px"
                  border-bottom-color="brand.purple"
                  focus-border-color="transparent"
                  v-model="searchTerm"
                  type="text"
                  autocomplete="off"
                  :placeholder="$t('productSearch.placeholder')"
                  @keydown="onSearchTermUpdated(searchTerm)"
                  @keyup="onSearchTermUpdated(searchTerm)"
                  @blur="onSearchTermUpdated(searchTerm)"
                  @keydown.esc="searchTerm = ''"
                  :_hover="{ borderBottomColor: 'brand.purple' }"
                  :_focus="{ borderBottomColor: 'brand.purple' }"
                  :_active="{ borderBottomColor: 'brand.purple' }"
                />
              </c-box>
              <c-box :d="{ base: 'block', md: 'none' }">
                <c-icon name="search" size="3rem" ml="md" color="brand.purple" />
              </c-box>
            </c-flex>
          </c-box>
          <c-box class="col-md-12">
            <c-box
              :m="{
                base: '0 -15px var(--chakra-space-sm) -15px',
                md: '0 var(--chakra-space-lg) 0 0',
              }"
            >
              <c-text as="span" v-if="isSearchRunning()">{{
                $t("productSearch.searching")
              }}</c-text>
              <c-text as="span" v-else>
                <c-text
                  as="span"
                  v-if="searchResult.count > 0"
                  :fontSize="{ base: '14px', md: '22px' }"
                  >{{ String($t("productSearch.searchResults")).replaceAll(":", "") }}
                  <!-- TODO remove semicolon from productSearch.searchResults translations and replaceAll() from the previous line after rebrand is in production -->
                  ({{ searchResult.count }})</c-text
                >
                <c-text as="span" v-else>{{ $t("productSearch.noResults") }}</c-text>
              </c-text>
            </c-box>
            <ProductList :class="getProductListClasses()"></ProductList>
            <InfiniteScroller
              :results="searchResult.value"
              :result-count="searchResult.count"
              :action="() => $store.dispatch('productSearch/loadMoreProducts')"
              :loading-more="loadingMore"
            />
          </c-box>
        </c-box>
      </c-box>
    </c-flex>
  </c-box>
</template>

<script lang="ts">
import InfiniteScroller from "@/components/InfiniteScroller.vue";
import CategoryFilter from "@/components/products/CategoryFilter.vue";
import Heading from "@/design-system/elements/Heading.vue";
import { CBox, CFlex, CHeading, CIcon, CInput, CText } from "@chakra-ui/vue";
import _ from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";
import ProductList from "../components/products/ProductList.vue";

export default defineComponent({
  name: "ProductSearch",
  components: {
    ProductList,
    CategoryFilter,
    Heading,
    InfiniteScroller,
    CIcon,
    CBox,
    CFlex,
    CText,
    CHeading,
    CInput,
  },
  computed: {
    ...mapState("productSearch", {
      searchQuery: "searchQuery",
      searchResult: "searchResult",
      searching: "searching",
      loadingMore: "loadingMore",
    }),
  },
  data() {
    return {
      searchTerm: "",
      lastSearchTerm: "x",
      typing: false,
      searchResults: { count: 0, facets: {}, value: [] },
    };
  },
  mounted() {
    this.searchTerm = this.searchQuery.term;
    this.lastSearchTerm = this.searchQuery.term;
    window.scrollTo(0, 0);
  },
  methods: {
    onSearchTermUpdated(term: string) {
      if (term.trim() !== (this.lastSearchTerm || "").trim()) {
        this.typing = true;

        // The following prevents wrong/old results to be displayed
        this.$store.dispatch("productSearch/updateTermWithoutSearching", term);

        this.search(term);
      }
    },
    search: _.debounce(function (this: any, term: string) {
      if (term !== this.lastSearchTerm) {
        this.lastSearchTerm = term;
        this.$store.dispatch("productSearch/updateTerm", term);
        this.typing = false;
      }
    }, 400),
    isSearchRunning() {
      return this.typing || this.searching;
    },
    getProductListClasses() {
      if (this.isSearchRunning()) {
        return "stale";
      }
      return "up-to-date";
    },
  },
});
</script>
